<script>
import { defineAsyncComponent, ref, computed } from 'vue';

import NextSidebar from 'next/sidebar/Sidebar.vue';
import ProductRail from 'dashboard/components-next/rail/ProductRail.vue';
import DashboardTopbar from 'dashboard/components-next/topbar/DashboardTopbar.vue';
import WootKeyShortcutModal from 'dashboard/components/widgets/modal/WootKeyShortcutModal.vue';
import AddAccountModal from 'dashboard/components/app/AddAccountModal.vue';
import UpgradePage from 'dashboard/routes/dashboard/upgrade/UpgradePage.vue';

import { useAccount } from 'dashboard/composables/useAccount';
import { findRailSectionForRoute } from 'dashboard/components-next/rail/sections';
import { useSidebarMode } from 'dashboard/components-next/rail/useSidebarMode';
import { setMobilePreviewArea } from 'dashboard/components-next/sidebar/useSidebarNavigation';

const CommandBar = defineAsyncComponent(
  () => import('./commands/commandbar.vue')
);

const FloatingCallWidget = defineAsyncComponent(
  () => import('dashboard/components-next/call/FloatingCallWidget.vue')
);

import CopilotLauncher from 'dashboard/components-next/copilot/CopilotLauncher.vue';
import CopilotContainer from 'dashboard/components/copilot/CopilotContainer.vue';

import { useCallsStore } from 'dashboard/stores/calls';

export default {
  components: {
    ProductRail,
    DashboardTopbar,
    NextSidebar,
    CommandBar,
    WootKeyShortcutModal,
    AddAccountModal,
    UpgradePage,
    CopilotLauncher,
    CopilotContainer,
    FloatingCallWidget,
  },
  setup() {
    const upgradePageRef = ref(null);
    const { accountId } = useAccount();
    const callsStore = useCallsStore();
    const { isMini, hasPanel } = useSidebarMode();

    return {
      isMini,
      hasPanel,
      accountId,
      upgradePageRef,
      hasActiveCall: computed(() => callsStore.hasActiveCall),
      hasIncomingCall: computed(() => callsStore.hasIncomingCall),
    };
  },
  data() {
    return {
      showCreateAccountModal: false,
      showShortcutModal: false,
      isMobileSidebarOpen: false,
      // Which section the open mobile drawer is showing; a second tap on the
      // same rail icon closes it.
      mobileDrawerSection: null,
    };
  },
  computed: {
    // Copilot assists with conversations, so it only belongs to the inbox
    // section. Mounted globally it followed the agent onto every screen.
    isInboxSection() {
      return findRailSectionForRoute(this.$route.name)?.name === 'inbox';
    },
    showUpgradePage() {
      return this.upgradePageRef?.shouldShowUpgradePage;
    },
    bypassUpgradePage() {
      return [
        'billing_settings_index',
        'settings_inbox_list',
        'general_settings_index',
        'agent_list',
      ].includes(this.$route.name);
    },
  },
  watch: {
    // Rail taps on mobile never navigate, so any route change means a link was
    // followed and the drawer's job is done.
    '$route.name'() {
      this.closeMobileSidebar();
    },
  },
  methods: {
    onRailSectionTap({ name, to }) {
      if (this.isMobileSidebarOpen && this.mobileDrawerSection === name) {
        this.closeMobileSidebar();
        return;
      }
      setMobilePreviewArea(name);
      // Wait for the previewed section's menu to report its link count: a
      // section with no panel (Team) navigates directly instead of opening an
      // empty drawer.
      this.$nextTick(() => {
        if (this.hasPanel) {
          this.mobileDrawerSection = name;
          this.isMobileSidebarOpen = true;
        } else {
          setMobilePreviewArea(null);
          this.mobileDrawerSection = null;
          this.isMobileSidebarOpen = false;
          this.$router.push(to);
        }
      });
    },
    closeMobileSidebar() {
      this.isMobileSidebarOpen = false;
      this.mobileDrawerSection = null;
      setMobilePreviewArea(null);
    },
    openCreateAccountModal() {
      this.showCreateAccountModal = true;
    },
    closeCreateAccountModal() {
      this.showCreateAccountModal = false;
    },
    toggleKeyShortcutModal() {
      this.showShortcutModal = true;
    },
    closeKeyShortcutModal() {
      this.showShortcutModal = false;
    },
  },
};
</script>

<template>
  <div class="relative flex flex-grow overflow-hidden text-n-slate-12">
    <ProductRail
      @show-create-account-modal="openCreateAccountModal"
      @section-tap="onRailSectionTap"
    />
    <!-- Holds the panel's 240px open in FULL and animates shut in MINI. The
         panel itself is positioned, so a hover-reveal never moves the content -->
    <div
      aria-hidden="true"
      class="hidden flex-shrink-0 transition-[width] duration-300 ease-out motion-reduce:transition-none shell:block"
      :class="isMini || !hasPanel ? 'w-0' : 'w-60'"
    />
    <NextSidebar
      :is-mobile-sidebar-open="isMobileSidebarOpen"
      @open-key-shortcut-modal="toggleKeyShortcutModal"
      @close-key-shortcut-modal="closeKeyShortcutModal"
      @close-mobile-sidebar="closeMobileSidebar"
    />

    <main
      class="flex flex-1 flex-col h-full w-full min-h-0 px-0 overflow-hidden bg-n-surface-1"
    >
      <DashboardTopbar />
      <section class="relative flex flex-1 min-h-0 w-full overflow-hidden">
        <UpgradePage
          v-show="showUpgradePage"
          ref="upgradePageRef"
          :bypass-upgrade-page="bypassUpgradePage"
        />
        <template v-if="!showUpgradePage">
          <router-view />
          <CommandBar />
          <CopilotLauncher v-if="isInboxSection" />
          <CopilotContainer v-if="isInboxSection" />
          <FloatingCallWidget v-if="hasActiveCall || hasIncomingCall" />
        </template>
        <AddAccountModal
          v-if="showCreateAccountModal"
          @close-account-create-modal="closeCreateAccountModal"
        />
        <WootKeyShortcutModal
          v-model:show="showShortcutModal"
          @close="closeKeyShortcutModal"
          @clickaway="closeKeyShortcutModal"
        />
      </section>
    </main>
  </div>
</template>
